import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Newspaper } from "lucide-react";
import api from "@/lib/api";
import SportBadge from "@/components/SportBadge";

const ROTATE_MS = 6000;

export default function HeadlinesStrip() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/headlines");
        if (mounted) setItems(data.headlines || []);
      } catch {
        /* strip stays hidden */
      }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    if (paused || items.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, items.length]);

  if (!items.length) return null;

  const active = idx % items.length;
  const featured = items[active];

  return (
    <section
      className="border-b border-bline bg-ink"
      data-testid="headlines-strip"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8 lg:px-12">
        <div className="mb-5 flex items-center gap-3">
          <Newspaper className="h-4 w-4 text-volt" />
          <p className="font-score text-xs tracking-[0.3em] text-volt">TOP DFW HEADLINES</p>
          <span className="hidden font-score text-[10px] tracking-widest text-slate-600 sm:inline">
            THE FRESHEST STORY FROM ALL 31 TEAM SITES
          </span>
        </div>

        <div className="min-h-[110px] sm:min-h-[92px]" data-testid="headline-featured">
          <AnimatePresence mode="wait">
            <motion.a
              key={`${featured.slug}-${featured.title}`}
              href={featured.url}
              target="_blank"
              rel="noreferrer"
              data-testid="headline-featured-link"
              className="group flex items-start gap-4 sm:items-center sm:gap-6"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              {featured.logo ? (
                <img src={featured.logo} alt="" className="h-12 w-12 shrink-0 object-contain sm:h-16 sm:w-16" />
              ) : (
                <span className="h-12 w-2 shrink-0 rounded-full sm:h-16" style={{ backgroundColor: featured.color }} />
              )}
              {featured.image && (
                <img src={featured.image} alt="" data-testid="headline-featured-image" className="hidden h-16 w-28 shrink-0 rounded-md border border-bline object-cover sm:block" />
              )}
              <span className="min-w-0">
                <span className="mb-1.5 flex flex-wrap items-center gap-2">
                  <span className="rounded-sm bg-bcast/20 px-1.5 py-0.5 font-display text-[10px] tracking-widest text-bcast">
                    {featured.league}
                  </span>
                  <span className="font-score text-[10px] tracking-widest text-slate-500">
                    {featured.name.toUpperCase()} · {featured.source.toUpperCase()}
                  </span>
                  <SportBadge sportLabel={featured.sport_label} testid="headline-featured-sport-badge" />
                </span>
                <span className="block font-display text-xl leading-tight text-white transition-colors duration-200 group-hover:text-volt sm:text-3xl">
                  {featured.title}
                </span>
                <span className="mt-1.5 inline-flex items-center gap-1 font-score text-[10px] tracking-widest text-slate-500 transition-colors group-hover:text-volt">
                  READ THE STORY
                  <ArrowUpRight className="h-3 w-3 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </span>
            </motion.a>
          </AnimatePresence>
        </div>

        <div className="mt-5 flex flex-wrap gap-1.5" data-testid="headline-dots">
          {items.map((h, i) => (
            <button
              key={h.slug}
              data-testid={`headline-dot-${h.slug}`}
              onClick={() => setIdx(i)}
              aria-label={`Feature ${h.name} headline`}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === active ? "w-6 bg-volt" : "w-2 bg-slate-700 hover:bg-slate-500"
              }`}
            />
          ))}
        </div>

      </div>
    </section>
  );
}
